/**
 * Billing & Usage Page
 *
 * Route: /settings/billing | Nav: Settings > Billing & Usage | Roles: TenantAdmin/Admin
 *
 * Subscription overview, usage gauges, usage history, per-tool breakdown, and invoices.
 */
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  Button,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableColumnDefinition,
  createTableColumn,
  ProgressBar,
  Tab,
  TabList,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
  ReceiptRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { billingApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { UnauthenticatedBanner } from '../components/common/UnauthenticatedBanner';
import { PageHeader } from '../components/common/PageHeader';
import {
  ListFrame,
  ListToolbar,
  Pager,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import type {
  UsageHistory,
  SubscriptionInfo,
  Invoice,
  RateLimitStatus,
} from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const formatDateTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatCurrency = (amount: number, currency?: string): string => {
  const cur = currency || 'USD';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(amount);
  } catch {
    return `${cur} ${amount.toFixed(2)}`;
  }
};

const getPlanColor = (plan: SubscriptionInfo['plan']): 'informative' | 'success' | 'important' => {
  switch (plan) {
    case 'Free':
      return 'informative';
    case 'Pro':
      return 'success';
    case 'Enterprise':
      return 'important';
    default:
      return 'informative';
  }
};

const getStatusColor = (
  status: SubscriptionInfo['status']
): 'success' | 'warning' | 'danger' | 'informative' => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Trial':
      return 'informative';
    case 'PastDue':
      return 'warning';
    case 'Cancelled':
      return 'danger';
    default:
      return 'informative';
  }
};

const getInvoiceStatusColor = (
  status: Invoice['status']
): 'success' | 'warning' | 'danger' | 'subtle' => {
  switch (status) {
    case 'Paid':
      return 'success';
    case 'Pending':
      return 'warning';
    case 'Overdue':
      return 'danger';
    case 'Void':
      return 'subtle';
    default:
      return 'warning';
  }
};

/** Wraps the (unpaged) `billingApi.getInvoices` response as a `PagedResult` — `useAdaptiveGridData`
 *  then applies search/sort/paging in memory (client mode). Unlike Usage History (a fixed 30-day
 *  report window) and Rate Limits (a fixed set of configured endpoints), Invoices is the one billing
 *  surface that grows without bound over the tenant's lifetime — the genuine list to migrate onto
 *  the List & Grid Standard. */
async function fetchInvoicesPage(params: ListQueryParams): Promise<PagedResult<Invoice>> {
  const items = await billingApi.getInvoices();
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

// ---------------------------------------------------------------------------
// BillingPage
// ---------------------------------------------------------------------------

export const BillingPage: React.FC = () => {
  const ps = usePageStyles();
  const [selectedTab, setSelectedTab] = useState<'overview' | 'history' | 'byTool' | 'invoices'>(
    'overview'
  );

  // Queries
  const { data: usage, isLoading: usageLoading, isError, error } = useQuery({
    queryKey: ['billingUsage'],
    queryFn: () => billingApi.getCurrentUsage(),
  });

  const { data: subscription, isLoading: subLoading } = useQuery({
    queryKey: ['billingSubscription'],
    queryFn: () => billingApi.getSubscription(),
  });

  const { data: rateLimits, isLoading: rateLimitsLoading } = useQuery({
    queryKey: ['billingRateLimits'],
    queryFn: () => billingApi.getRateLimits(),
    enabled: selectedTab === 'overview',
  });

  const { data: usageHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['billingUsageHistory'],
    queryFn: () => billingApi.getUsageHistory(30),
    enabled: selectedTab === 'history',
  });

  const { data: usageByTool, isLoading: byToolLoading } = useQuery({
    queryKey: ['billingUsageByTool'],
    queryFn: () => billingApi.getUsageByTool(),
    enabled: selectedTab === 'byTool',
  });

  // Invoices — migrated onto the List & Grid Standard (§ MIGRATION RECIPE): URL-state-backed
  // page/sort/search, adaptive client/server paging, four-state discipline. See `ApiKeysPage.tsx`
  // for the reference implementation this mirrors.
  const invoicesUrlState = useListUrlState();
  const [invoiceSortField, invoiceSortDirRaw] = invoicesUrlState.state.sort
    ? (invoicesUrlState.state.sort.split(':') as [string, string])
    : [undefined, undefined];
  const invoiceSortDir: SortDirection = invoiceSortDirRaw === 'desc' ? 'desc' : 'asc';
  const invoiceSortState: SortState[] | undefined = invoiceSortField
    ? [{ field: invoiceSortField, dir: invoiceSortDir }]
    : undefined;

  const toggleInvoiceSort = (field: string) => {
    if (field === invoiceSortField) {
      invoicesUrlState.setSort(`${field}:${invoiceSortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      invoicesUrlState.setSort(`${field}:asc`);
    }
  };

  const invoiceSortIcon = (field: string) => {
    if (field !== invoiceSortField) return <ArrowSort16Regular />;
    return invoiceSortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const invoicesGrid = useAdaptiveGridData<Invoice>({
    queryKeyBase: ['billing-invoices'],
    fetchPage: fetchInvoicesPage,
    page: invoicesUrlState.state.page,
    pageSize: invoicesUrlState.state.pageSize,
    search: invoicesUrlState.state.search,
    searchFields: ['number', 'status'],
    sortState: invoiceSortState,
    enabled: selectedTab === 'invoices',
  });

  const {
    items: invoices,
    totalCount: invoicesTotalCount,
    totalPages: invoicesTotalPages,
    hasPreviousPage: invoicesHasPreviousPage,
    hasNextPage: invoicesHasNextPage,
    isLoading: invoicesLoading,
    isFetching: invoicesFetching,
    isError: invoicesIsError,
    refetch: refetchInvoices,
  } = invoicesGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search.
  useEffect(() => {
    if (
      selectedTab === 'invoices' &&
      !invoicesLoading &&
      invoicesUrlState.state.page > invoicesTotalPages
    ) {
      invoicesUrlState.setPage(invoicesTotalPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, invoicesLoading, invoicesUrlState.state.page, invoicesTotalPages]);

  // Rate limit columns
  const rateLimitColumns: TableColumnDefinition<RateLimitStatus>[] = [
    createTableColumn<RateLimitStatus>({
      columnId: 'endpoint',
      compare: (a, b) => a.endpoint.localeCompare(b.endpoint),
      renderHeaderCell: () => 'Endpoint',
      renderCell: (item) => <Body1 style={{ fontFamily: 'monospace' }}>{item.endpoint}</Body1>,
    }),
    createTableColumn<RateLimitStatus>({
      columnId: 'limit',
      compare: (a, b) => a.limit - b.limit,
      renderHeaderCell: () => 'Limit',
      renderCell: (item) => <Body2>{item.limit.toLocaleString()}</Body2>,
    }),
    createTableColumn<RateLimitStatus>({
      columnId: 'remaining',
      compare: (a, b) => a.remaining - b.remaining,
      renderHeaderCell: () => 'Remaining',
      renderCell: (item) => (
        <Body2
          style={{
            color:
              item.remaining < item.limit * 0.1
                ? tokens.colorPaletteRedForeground1
                : tokens.colorNeutralForeground1,
          }}
        >
          {item.remaining.toLocaleString()}
        </Body2>
      ),
    }),
    createTableColumn<RateLimitStatus>({
      columnId: 'resetsAt',
      compare: (a, b) => new Date(a.resetsAt).getTime() - new Date(b.resetsAt).getTime(),
      renderHeaderCell: () => 'Resets At',
      renderCell: (item) => <Body2>{formatDateTime(item.resetsAt)}</Body2>,
    }),
  ];

  // Usage history columns
  const historyColumns: TableColumnDefinition<UsageHistory>[] = [
    createTableColumn<UsageHistory>({
      columnId: 'date',
      compare: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      renderHeaderCell: () => 'Date',
      renderCell: (item) => <Body2>{formatDate(item.date)}</Body2>,
    }),
    createTableColumn<UsageHistory>({
      columnId: 'scans',
      compare: (a, b) => a.scans - b.scans,
      renderHeaderCell: () => 'Scans',
      renderCell: (item) => <Body2>{item.scans.toLocaleString()}</Body2>,
    }),
    createTableColumn<UsageHistory>({
      columnId: 'apiCalls',
      compare: (a, b) => a.apiCalls - b.apiCalls,
      renderHeaderCell: () => 'API Calls',
      renderCell: (item) => <Body2>{item.apiCalls.toLocaleString()}</Body2>,
    }),
    createTableColumn<UsageHistory>({
      columnId: 'findings',
      compare: (a, b) => a.findings - b.findings,
      renderHeaderCell: () => 'Findings',
      renderCell: (item) => <Body2>{item.findings.toLocaleString()}</Body2>,
    }),
  ];

  // Invoices list body — four-state discipline (§ LIST & GRID STANDARD): loading skeleton ->
  // error -> empty -> loaded (hand-composed <Table>, no Fluent DataGrid). No Actions column:
  // the invoice list has no mutating row actions (Download is a plain external link, not a
  // mutation) per `billingApi` / `BillingController` — every endpoint it calls is a GET.
  let invoiceListBody: ReactNode;
  if (invoicesLoading) {
    invoiceListBody = <LoadingSkeleton rows={5} columns={5} />;
  } else if (invoicesIsError) {
    invoiceListBody = <ErrorState message="Unable to load invoices." onRetry={refetchInvoices} />;
  } else if (invoices.length === 0) {
    invoiceListBody = (
      <EmptyState
        icon={<ReceiptRegular />}
        title="No Invoices"
        description="No billing activity yet."
      />
    );
  } else {
    invoiceListBody = (
      <ListLoadingOverlay busy={invoicesFetching && !invoicesLoading}>
        <ListFrame>
          <Table aria-label="Invoices">
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  sortable
                  sortDirection={
                    invoiceSortField === 'number'
                      ? invoiceSortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  sortIcon={invoiceSortIcon('number')}
                  onClick={() => toggleInvoiceSort('number')}
                >
                  Number
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  sortDirection={
                    invoiceSortField === 'date'
                      ? invoiceSortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  sortIcon={invoiceSortIcon('date')}
                  onClick={() => toggleInvoiceSort('date')}
                >
                  Date
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  sortDirection={
                    invoiceSortField === 'amount'
                      ? invoiceSortDir === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  sortIcon={invoiceSortIcon('amount')}
                  onClick={() => toggleInvoiceSort('amount')}
                >
                  Amount
                </TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Body1 style={{ fontWeight: 500 }}>{item.number}</Body1>
                  </TableCell>
                  <TableCell>
                    <Body2>{formatDate(item.date)}</Body2>
                  </TableCell>
                  <TableCell>
                    <Body1>{formatCurrency(item.amount, item.currency)}</Body1>
                  </TableCell>
                  <TableCell>
                    <Badge appearance="filled" color={getInvoiceStatusColor(item.status)}>
                      {item.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {item.downloadUrl ? (
                      <Button
                        appearance="subtle"
                        icon={<ArrowDownloadRegular />}
                        size="small"
                        as="a"
                        href={item.downloadUrl}
                        target="_blank"
                      >
                        Download
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ListFrame>
      </ListLoadingOverlay>
    );
  }

  // Loading state
  if (usageLoading || subLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading billing information..." />
      </div>
    );
  }

  const queryError = error as any;
  const isUnauth = queryError?.status === 401 || queryError?.message?.includes('MSAL');
  if (isError && isUnauth) {
    return (
      <div className={ps.page}>
        <PageHeader title="Billing & Usage" subtitle="Subscription management, usage tracking, and invoicing" />
        <UnauthenticatedBanner feature="billing information" />
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <PageHeader title="Billing & Usage" subtitle="Subscription management, usage tracking, and invoicing" />

      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) =>
          setSelectedTab(data.value as typeof selectedTab)
        }
      >
        <Tab value="overview">Overview</Tab>
        <Tab value="history">Usage History</Tab>
        <Tab value="byTool">By Tool</Tab>
        <Tab value="invoices">Invoices</Tab>
      </TabList>

      {/* Overview Tab */}
      {selectedTab === 'overview' && (
        <>
          {/* Subscription Card */}
          {subscription && (
            <Card>
              <CardHeader
                header={<Title2>Subscription</Title2>}
                description="Current plan and status"
              />
              <div className={ps.cardBody}>
                <div className={ps.inlineRow}>
                  <Badge appearance="filled" color={getPlanColor(subscription.plan)}>
                    {subscription.plan}
                  </Badge>
                  <Badge appearance="filled" color={getStatusColor(subscription.status)}>
                    {subscription.status}
                  </Badge>
                </div>
                <div className={ps.inlineRow}>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                    Period: {formatDate(subscription.currentPeriodStart)} -{' '}
                    {formatDate(subscription.currentPeriodEnd)}
                  </Body2>
                </div>
                {subscription.monthlyPrice !== undefined && (
                  <Body1 style={{ fontWeight: 600 }}>
                    {formatCurrency(subscription.monthlyPrice, subscription.currency)} / month
                  </Body1>
                )}
                {subscription.trialEndsAt && (
                  <Body2 style={{ color: tokens.colorPaletteYellowForeground1 }}>
                    Trial ends: {formatDate(subscription.trialEndsAt)}
                  </Body2>
                )}
              </div>
            </Card>
          )}

          {/* Usage Gauges */}
          {usage && (
            <Card>
              <CardHeader
                header={<Title2>Current Usage</Title2>}
                description={`${formatDate(usage.currentPeriodStart)} - ${formatDate(usage.currentPeriodEnd)}`}
              />
              <div className={ps.cardBody}>
                {/* Scans */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: tokens.spacingVerticalXS,
                    }}
                  >
                    <Body1>Scans</Body1>
                    <Body2>
                      {usage.scansUsed.toLocaleString()} / {usage.scansLimit.toLocaleString()}
                    </Body2>
                  </div>
                  <ProgressBar
                    value={usage.scansLimit > 0 ? usage.scansUsed / usage.scansLimit : 0}
                    thickness="large"
                    color={
                      usage.scansUsed / usage.scansLimit > 0.9 ? 'error' : 'brand'
                    }
                  />
                </div>
                {/* API Calls */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: tokens.spacingVerticalXS,
                    }}
                  >
                    <Body1>API Calls</Body1>
                    <Body2>
                      {usage.apiCallsUsed.toLocaleString()} / {usage.apiCallsLimit.toLocaleString()}
                    </Body2>
                  </div>
                  <ProgressBar
                    value={usage.apiCallsLimit > 0 ? usage.apiCallsUsed / usage.apiCallsLimit : 0}
                    thickness="large"
                    color={
                      usage.apiCallsUsed / usage.apiCallsLimit > 0.9 ? 'error' : 'brand'
                    }
                  />
                </div>
                {/* Storage */}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: tokens.spacingVerticalXS,
                    }}
                  >
                    <Body1>Storage</Body1>
                    <Body2>
                      {usage.storageUsedMb.toLocaleString()} MB / {usage.storageLimitMb.toLocaleString()} MB
                    </Body2>
                  </div>
                  <ProgressBar
                    value={
                      usage.storageLimitMb > 0 ? usage.storageUsedMb / usage.storageLimitMb : 0
                    }
                    thickness="large"
                    color={
                      usage.storageUsedMb / usage.storageLimitMb > 0.9 ? 'error' : 'brand'
                    }
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Rate Limits */}
          <Card>
            <CardHeader
              header={<Title2>Rate Limits</Title2>}
              description="Current API rate limit status"
            />
            {rateLimitsLoading ? (
              <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' }}>
                <Spinner label="Loading rate limits..." />
              </div>
            ) : rateLimits && rateLimits.length > 0 ? (
              <DataGrid
                items={rateLimits}
                columns={rateLimitColumns}
                sortable
                getRowId={(item) => item.endpoint}
                style={{ minWidth: '600px' }}
              >
                <DataGridHeader>
                  <DataGridRow>
                    {({ renderHeaderCell }) => (
                      <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody<RateLimitStatus>>
                  {({ item, rowId }) => (
                    <DataGridRow<RateLimitStatus> key={rowId}>
                      {({ renderCell }) => (
                        <DataGridCell>{renderCell(item)}</DataGridCell>
                      )}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            ) : (
              <Body1 className={ps.emptyState}>Usage tracking activates after your first API call.</Body1>
            )}
          </Card>
        </>
      )}

      {/* Usage History Tab */}
      {selectedTab === 'history' && (
        <Card>
          <CardHeader
            header={<Title2>Usage History</Title2>}
            description="Daily usage over the last 30 days"
          />
          {historyLoading ? (
            <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' }}>
              <Spinner label="Loading usage history..." />
            </div>
          ) : usageHistory && usageHistory.length > 0 ? (
            <DataGrid
              items={usageHistory}
              columns={historyColumns}
              sortable
              getRowId={(item) => item.date}
              style={{ minWidth: '500px' }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<UsageHistory>>
                {({ item, rowId }) => (
                  <DataGridRow<UsageHistory> key={rowId}>
                    {({ renderCell }) => (
                      <DataGridCell>{renderCell(item)}</DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          ) : (
            <Body1 className={ps.emptyState}>Usage history builds as you run scans and make API calls.</Body1>
          )}
        </Card>
      )}

      {/* By Tool Tab */}
      {selectedTab === 'byTool' && (
        <Card>
          <CardHeader
            header={<Title2>Usage by Sensor Tool</Title2>}
            description="Breakdown of usage across sensor types"
          />
          {byToolLoading ? (
            <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' }}>
              <Spinner label="Loading tool usage..." />
            </div>
          ) : usageByTool && Object.keys(usageByTool).length > 0 ? (
            <div className={ps.cardBody}>
              <div className={ps.statGrid}>
                {Object.entries(usageByTool).map(([tool, count]) => (
                  <div key={tool} className={ps.statTile}>
                    <Title2>{count.toLocaleString()}</Title2>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{tool}</Body2>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Body1 className={ps.emptyState}>Tool usage data appears after your first scan completes.</Body1>
          )}
        </Card>
      )}

      {/* Invoices Tab — migrated onto the List & Grid Standard. `ListFrame` (not `ListPageLayout`)
          since this is a list embedded in a tab of a larger dashboard page, not a dedicated list
          page — see `.github/context/list-grid-standard.context.md` §2. */}
      {selectedTab === 'invoices' && (
        <Card>
          <CardHeader
            header={<Title2>Invoices</Title2>}
            description="Billing history and downloadable invoices"
          />
          <div className={ps.cardBody}>
            <ListToolbar
              search={invoicesUrlState.state.search}
              onSearchChange={invoicesUrlState.setSearch}
              searchPlaceholder="Search invoices…"
              onRefresh={refetchInvoices}
              isRefreshing={invoicesFetching}
              testIdPrefix="invoices"
            />
            {invoiceListBody}
            {!invoicesLoading && !invoicesIsError && invoices.length > 0 && (
              <Pager
                page={invoicesUrlState.state.page}
                totalPages={invoicesTotalPages}
                hasPreviousPage={invoicesHasPreviousPage}
                hasNextPage={invoicesHasNextPage}
                totalCount={invoicesTotalCount}
                onPageChange={invoicesUrlState.setPage}
                testId="invoices-pager"
              />
            )}
          </div>
        </Card>
      )}
    </div>
  );
};
